import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import SnackbarUtils from "../../libs/SnackbarUtils";
import { resetForm, updateForm } from "../../actions/UserForm.action";
import {
  serviceGetUserDashboardEarned,
  serviceGetUserDashboardEarnedTable,
  serviceGetUserDashboardEngagementTable,
  serviceGetUserDashboardPet,
  serviceGetUserDashboardRedeemed,
  serviceGetUserDashboardRedeemedTable,
  serviceGetUserDashboardReward,
  serviceGetUserDashboardTrend,
  serviceGetUserDashboardRadarChart,
  serviceProgramRedemptionTrendExport,
  serviceProgramEngagementTrendExport,
} from "../../services/UserDashboard.service";
import history from "../../libs/history.utils";
import RouteName from "../../routes/Route.name";
import moment from "moment";

function useUserDashboard() {
  const [rewardData, setRewardData] = useState({});
  const [petData, setPetData] = useState([]);
  const [trendData, setTrendData] = useState({});
  const [earnedData, setEarnedData] = useState([]);
  const [redeemedData, setRedeemedData] = useState([]);
  const [earnerTable, setEarnerTable] = useState([]);
  const [redeemedTable, setRedeemedTable] = useState([]);
  const [engagementTable, setEngagementTable] = useState([]);
  const [graphData, setGraphData] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [expandedView, setExpandedView] = useState({
    open: false,
    title: "",
    content: null,
  });
  const dispatch = useDispatch();
  const form = useSelector((state) => state.user_form);
  const {
    user: { client_ids },
  } = useSelector((state) => state.auth);

  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    if (client_ids) {
      changeTextData(client_ids[0], "client_id");
    }
  }, [client_ids]);

  const changeTextData = useCallback(
    (text, fieldName) => {
      const updatedForm = { ...form };
      if (fieldName === "state_ids") {
        updatedForm["city_ids"] = [];
        updatedForm[fieldName] = text;
      } else if (fieldName === "client_id") {
        updatedForm["program_ids"] = [];
        updatedForm["city_ids"] = [];
        updatedForm["state_ids"] = [];
        updatedForm[fieldName] = text;
      } else if (fieldName === "program_ids") {
        updatedForm["city_ids"] = [];
        updatedForm["state_ids"] = [];
        updatedForm[fieldName] = text;
      } else if (fieldName === "start_date" || fieldName === "end_date") {
        const selectedDate = new Date(text);
        if (fieldName === "end_date") {
          const today = new Date();
          today.setHours(23, 59, 59, 999);
          if (selectedDate > today) {
            SnackbarUtils.error("End date should not be greater than today");
            updatedForm["end_date"] = "";
          } else {
            updatedForm[fieldName] = selectedDate;
          }
        } else {
          updatedForm[fieldName] = selectedDate;
        }
      } else {
        updatedForm[fieldName] = text;
      }
      if (updatedForm?.start_date && updatedForm?.end_date) {
        const startDate = new Date(updatedForm?.start_date);
        const endDate = new Date(updatedForm?.end_date);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);
        if (startDate.getTime() > endDate.getTime()) {
          SnackbarUtils.error("End Date cannot be less than Start Date");
          updatedForm["end_date"] = "";
        }
      }

      dispatch(updateForm(updatedForm));
    },
    [form, dispatch]
  );

  const isFormComplete = useCallback(() => {
    return Object.entries(form).every(([key, value]) =>
      Array.isArray(value) ? value.length > 0 : Boolean(value)
    );
  }, [form]);

  const renderTotalReward = useCallback(
    (data) => {
      let req = serviceGetUserDashboardReward({
        ...data,
      });
      req.then((res) => {
        if (!res?.error) {
          const data = res?.data;
          setRewardData(data);
        }
      });
    },
    [setRewardData]
  );

  const renderGraphData = useCallback(
    (data) => {
      let req = serviceGetUserDashboardRadarChart({
        ...data,
      });
      req.then((res) => {
        if (!res?.error) {
          const data = res?.data;
          setGraphData(data);
        }
      });
    },
    [setGraphData]
  );

  const renderPetInfo = useCallback(
    (data) => {
      let req = serviceGetUserDashboardPet({
        ...data,
      });
      req.then((res) => {
        if (!res?.error) {
          const data = res?.data;
          setPetData(data);
        }
      });
    },
    [setPetData]
  );

  const renderTrendInfo = useCallback(
    (data) => {
      let req = serviceGetUserDashboardTrend({
        ...data,
      });
      req.then((res) => {
        if (!res?.error) {
          const data = res?.data;
          setTrendData(data);
        }
      });
    },
    [setTrendData]
  );

  const renderEarnedInfo = useCallback(
    (data) => {
      let req = serviceGetUserDashboardEarned({
        ...data,
      });
      req.then((res) => {
        if (!res?.error) {
          const data = res?.data;
          const updatedData = data?.map((val) => {
            return {
              label: val?.label,
              ACTION: val?.ACTION,
              LEARNING: val?.LEARNING,
              OTHER: val?.OTHER,
              QUIZ: val?.QUIZ,
              STREAK: val?.STREAK,
              VIDEO: val?.VIDEO,
            };
          });
          setEarnedData(updatedData);
        }
      });
    },
    [setEarnedData]
  );

  const renderRedeemedInfo = useCallback(
    (data) => {
      let req = serviceGetUserDashboardRedeemed({
        ...data,
      });
      req.then((res) => {
        if (!res?.error) {
          const data = res?.data;
          data?.forEach((val) => {
            delete val?.date;
          });
          setRedeemedData(data);
        }
      });
    },
    [setRedeemedData]
  );

  const renderEarnedTable = useCallback(
    (data) => {
      let req = serviceGetUserDashboardEarnedTable({
        ...data,
      });
      req.then((res) => {
        if (!res?.error) {
          const data = res?.data;
          setEarnerTable(data);
        }
      });
    },
    [setEarnerTable]
  );

  const renderRedeemedTable = useCallback(
    (data) => {
      let req = serviceGetUserDashboardRedeemedTable({
        ...data,
      });
      req.then((res) => {
        if (!res?.error) {
          const data = res?.data;
          setRedeemedTable(data);
        }
      });
    },
    [setRedeemedTable]
  );

  const renderEngagementTable = useCallback(
    (data) => {
      let req = serviceGetUserDashboardEngagementTable({
        ...data,
      });
      req.then((res) => {
        if (!res?.error) {
          const data = res?.data;
          setEngagementTable(data);
        }
      });
    },
    [setEngagementTable]
  );
  useEffect(() => {
    if (isFormComplete()) {
      const updatedForm = {};

      for (const key in form) {
        const value = form[key];
        if (key === "program_ids") {
          updatedForm[key] = Array.isArray(value)
            ? value?.map((val) => val?.id)
            : [];
        } else if (["state_ids", "city_ids"].includes(key)) {
          const ids = Array.isArray(value) ? value?.map((val) => val?.id) : [];
          const hasAllFlag =
            ids?.includes("ALL_CITIES") || ids?.includes("PAN_INDIA");
          updatedForm[key] = hasAllFlag ? [] : ids;
        } else if (["start_date", "end_date"]?.includes(key)) {
          updatedForm[key] = value ? moment(value)?.format("YYYY-MM-DD") : "";
        } else {
          updatedForm[key] = value ?? "";
        }
      }
      setFormValues(updatedForm);
      renderTotalReward(updatedForm);
      renderPetInfo(updatedForm);
      renderTrendInfo(updatedForm);
      renderEarnedInfo(updatedForm);
      renderRedeemedInfo(updatedForm);
      renderEarnedTable(updatedForm);
      renderRedeemedTable(updatedForm);
      renderEngagementTable(updatedForm);
      renderPetInfo(updatedForm);
      renderGraphData(updatedForm);
    }
  }, [form]);

  const handleDetail = useCallback((data) => {
    history.push(RouteName.APP_DETAIL + data?.id);
  }, []);

  const handleReset = useCallback(() => {
    dispatch(resetForm());
    setRewardData({});
    setPetData([]);
    setTrendData({});
    setEarnedData([]);
    setRedeemedData([]);
    setEarnerTable([]);
    setRedeemedTable([]);
    setEngagementTable([]);
    setGraphData([]);
  }, []);

  const handleExpand = useCallback((title, component) => {
    setExpandedView({
      open: true,
      title,
      content: component,
    });
  }, []);

  const downloadBlob = useCallback((blob, fileName) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, []);

  const handleProgramEngTrendExport = useCallback(() => {
    try {
      serviceProgramEngagementTrendExport({ ...formValues }, user?.token).then(
        async (res) => {
          const blob = await res.blob();
          downloadBlob(blob, "program_performance_report.csv");
        }
      );
    } catch (e) {
      console.log("error", e);
    }
  }, [formValues]);

  const handleProgramRedTrendExport = useCallback(() => {
    try {
      serviceProgramRedemptionTrendExport({ ...formValues }, user?.token).then(
        async (res) => {
          const blob = await res.blob();
          downloadBlob(blob, "program_performance_report.csv");
        }
      );
    } catch (e) {
      console.log("error", e);
    }
  }, [formValues]);

  return {
    form,
    changeTextData,
    rewardData,
    petData,
    earnedData,
    redeemedData,
    earnerTable,
    handleDetail,
    redeemedTable,
    engagementTable,
    petData,
    trendData,
    graphData,
    handleReset,
    expandedView,
    handleExpand,
    handleProgramEngTrendExport,
    handleProgramRedTrendExport,
    setExpandedView,
  };
}

export default useUserDashboard;
